import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from '../components/Icon';
import ConfirmDialog from '../components/ConfirmDialog';
import StripeWebhookSetup from '../components/StripeWebhookSetup';
import { useToast } from '../components/Toast';
import {
  StripeKeyName,
  StripeMode,
  StripeSettings as StripeSettingsData,
  StripeSettingsUpdate,
  getStripeSettings,
  updateStripeSettings,
} from '../api/stripeSettings';
import { extractApiError } from '../api/legalServices';

const MODES: StripeMode[] = ['test', 'live'];
const KEYS: StripeKeyName[] = ['publishableKey', 'secretKey', 'webhookSecret'];

// Mirrors the backend check in services/stripeConfig.js.
const PREFIXES: Record<StripeMode, Record<StripeKeyName, string[]>> = {
  test: { publishableKey: ['pk_test_'], secretKey: ['sk_test_', 'rk_test_'], webhookSecret: ['whsec_'] },
  live: { publishableKey: ['pk_live_'], secretKey: ['sk_live_', 'rk_live_'], webhookSecret: ['whsec_'] },
};

const MODE_INFO: Record<
  StripeMode,
  { badge: string; color: string; bg: string; title: string; text: string; icon: string; section: string }
> = {
  test: {
    badge: 'Test',
    color: 'var(--yellow)',
    bg: 'var(--yellow-bg)',
    title: 'Sandbox / Test mode',
    text: 'Uses Stripe test cards — no real money moves. For development and QA.',
    icon: 'flask-conical',
    section: 'Test credentials',
  },
  live: {
    badge: 'Live',
    color: 'var(--green)',
    bg: 'var(--green-bg)',
    title: 'Live production mode',
    text: 'Charges real cards. Switch once your live keys are saved.',
    icon: 'shield',
    section: 'Live credentials',
  },
};

const FIELD_LABEL: Record<StripeKeyName, string> = {
  publishableKey: 'Publishable key',
  secretKey: 'Secret key',
  webhookSecret: 'Webhook signing secret',
};

type Draft = Record<StripeMode, Record<StripeKeyName, string>>;

/*
 * Publishable keys are edited in place. Secrets start blank because the
 * server only ever sends them masked — a blank secret means "keep the saved
 * one", and typing a value replaces it.
 */
const draftFrom = (s: StripeSettingsData): Draft => ({
  test: { publishableKey: s.test.publishableKey, secretKey: '', webhookSecret: '' },
  live: { publishableKey: s.live.publishableKey, secretKey: '', webhookSecret: '' },
});

const prefixError = (mode: StripeMode, key: StripeKeyName, value: string): string | null => {
  const v = value.trim();
  if (!v) return null;
  const allowed = PREFIXES[mode][key];
  return allowed.some((p) => v.startsWith(p)) ? null : `Must start with ${allowed.join(' or ')}`;
};

const shortKey = (key: string): string =>
  key.length > 22 ? `${key.slice(0, 14)}…${key.slice(-4)}` : key;

// ─── Small pieces ──────────────────────────────────────────────────

const ModeBadge: React.FC<{ mode: StripeMode }> = ({ mode }) => (
  <span
    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider"
    style={{ background: MODE_INFO[mode].bg, color: MODE_INFO[mode].color }}
  >
    {MODE_INFO[mode].badge}
  </span>
);

// Keys never contain whitespace; dropping it also cleans up pasted newlines.
const cleanKey = (v: string): string => v.replace(/\s+/g, '');

interface PublishableKeyInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  invalid: boolean;
}

/** Wraps instead of scrolling so a full ~110-character key stays readable. */
const PublishableKeyInput: React.FC<PublishableKeyInputProps> = ({
  value,
  onChange,
  placeholder,
  invalid,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, []);
  useLayoutEffect(fit, [value, fit]);
  // Re-fit whenever the width changes: window resizes, and the Settings tab
  // panel being un-hidden (a hidden textarea measures 0 and stays collapsed).
  // Deferred a frame so resizing the observed element doesn't trip the
  // "ResizeObserver loop" error.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let width = -1;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === width) return;
      width = entry.contentRect.width;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [fit]);

  return (
    <textarea
      ref={ref}
      rows={1}
      className="form-input font-mono text-[13px] leading-relaxed resize-none overflow-hidden block"
      style={{ wordBreak: 'break-all', ...(invalid ? { borderColor: 'var(--red)' } : {}) }}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(cleanKey(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
      }}
      autoComplete="off"
      spellCheck={false}
    />
  );
};

const FieldHint: React.FC<{ error?: string | null; children?: React.ReactNode }> = ({
  error,
  children,
}) =>
  error || children ? (
    <div className="text-[12px] mt-1.5" style={{ color: error ? 'var(--red)' : 'var(--g500)' }}>
      {error || children}
    </div>
  ) : null;

interface SecretKeyFieldProps {
  /** Masked saved value from the server ('' if none is saved). */
  saved: string;
  /** New value being typed ('' = keep the saved one). */
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  error: string | null;
  /** Hint under the field while nothing is saved. */
  emptyHint: string;
}

/*
 * A saved secret shows as a filled, locked value with a Replace button —
 * the server only ever sends it masked. Replacing (or a secret that was
 * never saved) gives a normal input with a show/hide toggle.
 */
const SecretKeyField: React.FC<SecretKeyFieldProps> = ({
  saved,
  value,
  onChange,
  placeholder,
  error,
  emptyHint,
}) => {
  const [replacing, setReplacing] = useState(false);
  const [show, setShow] = useState(false);

  if (saved && !replacing) {
    return (
      <>
        <div className="flex items-center gap-2.5 rounded-[10px] border border-g200 bg-g50 pl-3.5 pr-1.5 py-1.5">
          <Icon name="lock" size={15} color="var(--green)" />
          <span className="font-mono text-[13px] text-ink flex-1 min-w-0 truncate">{saved}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setReplacing(true)}
          >
            <Icon name="pencil" size={13} />
            Replace
          </button>
        </div>
        <FieldHint>Saved. Hidden for security — use Replace to enter a new one.</FieldHint>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <input
            type={show ? 'text' : 'password'}
            className="form-input font-mono text-[13px] pr-11"
            style={error ? { borderColor: 'var(--red)' } : undefined}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(cleanKey(e.target.value))}
            // Keep the browser from autofilling the admin's login password here.
            autoComplete="new-password"
            spellCheck={false}
            autoFocus={replacing}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-g100"
            aria-label={show ? 'Hide value' : 'Show value'}
          >
            <Icon name={show ? 'eye-off' : 'eye'} size={16} color="var(--g500)" />
          </button>
        </div>
        {replacing && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onChange('');
              setReplacing(false);
              setShow(false);
            }}
          >
            Cancel
          </button>
        )}
      </div>
      <FieldHint error={error}>
        {replacing ? 'Paste the new key. Cancel keeps the saved one.' : emptyHint}
      </FieldHint>
    </>
  );
};

const StatusRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-3 px-4 py-3 rounded-xl bg-g50 border border-g100">
    <span className="text-[13px] text-g600 font-medium flex-shrink-0">{label}</span>
    <span className="text-[13px] text-ink font-semibold text-right min-w-0 truncate">{children}</span>
  </div>
);

// ─── Page ──────────────────────────────────────────────────────────

const StripeSettings: React.FC = () => {
  const { toast } = useToast();
  const [saved, setSaved] = useState<StripeSettingsData | null>(null);
  const [mode, setMode] = useState<StripeMode>('test');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmLive, setConfirmLive] = useState<boolean>(false);
  // Bumped on load/save/discard to remount the key fields, which drops any
  // open "Replace" editors and show/hide toggles.
  const [formKey, setFormKey] = useState<number>(0);

  const apply = useCallback((s: StripeSettingsData) => {
    setSaved(s);
    setMode(s.mode);
    setDraft(draftFrom(s));
    setFormKey((k) => k + 1);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      apply(await getStripeSettings());
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load Stripe settings'));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="card text-center text-g500" style={{ padding: '64px 32px' }}>
        <Icon name="key" size={28} color="var(--g400)" /> Loading Stripe settings...
      </div>
    );
  }

  if (loadError || !saved || !draft) {
    return (
      <div
        className="card"
        style={{
          padding: 24,
          background: 'var(--red-bg)',
          color: 'var(--red)',
          border: '1px solid rgba(220,38,38,.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load Stripe settings</strong>
        </div>
        <div className="text-sm mb-3">{loadError}</div>
        <button className="btn btn-secondary btn-sm" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const isChanged = (m: StripeMode, k: StripeKeyName): boolean =>
    k === 'publishableKey'
      ? draft[m][k].trim() !== saved[m][k]
      : draft[m][k].trim() !== '';

  // Whether a key will be present once the current edits are saved.
  const willHave = (m: StripeMode, k: StripeKeyName): boolean =>
    isChanged(m, k) ? draft[m][k].trim() !== '' : saved[m][k] !== '';

  // Same rule as the backend: you can't switch to a mode that's missing its
  // two keys, or strip a key from the active mode. Saving other keys while
  // the active mode is still incomplete (e.g. a fresh install) is fine.
  const modeComplete = willHave(mode, 'publishableKey') && willHave(mode, 'secretKey');
  const savedComplete = saved[mode].publishableKey !== '' && saved[mode].secretKey !== '';
  const modeReady = modeComplete || (mode === saved.mode && !savedComplete);
  const hasErrors = MODES.some((m) => KEYS.some((k) => prefixError(m, k, draft[m][k])));
  const dirty = mode !== saved.mode || MODES.some((m) => KEYS.some((k) => isChanged(m, k)));

  const setField = (m: StripeMode, k: StripeKeyName, v: string) =>
    setDraft((d) => (d ? { ...d, [m]: { ...d[m], [k]: v } } : d));

  const save = async () => {
    const payload: StripeSettingsUpdate = {};
    if (mode !== saved.mode) payload.mode = mode;
    for (const m of MODES) {
      for (const k of KEYS) {
        if (isChanged(m, k)) payload[m] = { ...payload[m], [k]: draft[m][k].trim() };
      }
    }

    setSaving(true);
    try {
      const updated = await updateStripeSettings(payload);
      apply(updated);
      toast(
        payload.mode
          ? `Stripe is now in ${payload.mode} mode`
          : 'Stripe settings saved',
        'success'
      );
    } catch (err) {
      toast(extractApiError(err, 'Failed to save Stripe settings'), 'error');
    } finally {
      setSaving(false);
      setConfirmLive(false);
    }
  };

  const handleSave = () => {
    if (!dirty || hasErrors || !modeReady || saving) return;
    if (mode === 'live' && saved.mode !== 'live') {
      setConfirmLive(true);
      return;
    }
    save();
  };

  const active = saved[saved.mode];

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px] items-start">
      {/* ── Main form ── */}
      <div className="card p-6 animate-fadeIn">
        <div className="text-[15px] font-bold mb-4">Active Stripe mode</div>
        <div className="grid gap-4 sm:grid-cols-2" role="radiogroup" aria-label="Active Stripe mode">
          {MODES.map((m) => {
            const selected = mode === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setMode(m)}
                className="text-left rounded-2xl p-4 transition-all"
                style={{
                  border: selected ? '1.5px solid var(--brand)' : '1.5px solid var(--g200)',
                  background: selected ? 'var(--brand-light)' : '#fff',
                }}
              >
                <div className="flex justify-between items-start mb-2.5">
                  <ModeBadge mode={m} />
                  <span
                    className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ border: `2px solid ${selected ? 'var(--brand)' : 'var(--g300)'}` }}
                  >
                    {selected && <span className="w-2 h-2 rounded-full bg-brand" />}
                  </span>
                </div>
                <div className="text-[15px] font-bold text-ink">{MODE_INFO[m].title}</div>
                <div className="text-[12.5px] text-g500 mt-1 leading-snug">{MODE_INFO[m].text}</div>
              </button>
            );
          })}
        </div>

        {!modeComplete && (
          <div
            className="flex items-start gap-2 mt-4 px-4 py-3 rounded-xl text-[13px]"
            style={{ background: 'var(--yellow-bg)', color: 'var(--yellow)' }}
          >
            <Icon name="info" size={16} color="var(--yellow)" />
            <span>
              {modeReady
                ? `Payments are off until ${mode} mode has a publishable key and a secret key.`
                : `Add the ${mode} publishable key and secret key below before making ${mode} mode active.`}
            </span>
          </div>
        )}

        {MODES.map((m) => (
          <div key={m} className="mt-6 pt-6 border-t border-g100">
            <div className="flex items-center gap-2 mb-4">
              <Icon name={MODE_INFO[m].icon} size={16} color={MODE_INFO[m].color} />
              <span className="text-[12.5px] font-bold uppercase tracking-wider text-g700">
                {MODE_INFO[m].section}
              </span>
              {saved.mode === m && (
                <span className="text-[11px] font-semibold text-brand ml-1">· in use</span>
              )}
            </div>
            <div key={formKey} className="grid gap-x-5 md:grid-cols-2">
              {KEYS.map((k) => {
                const stored = saved[m][k];
                const error = prefixError(m, k, draft[m][k]);
                const example = `${PREFIXES[m][k][0]}...`;
                return (
                  <div
                    key={k}
                    className={'mb-4 min-w-0' + (k === 'publishableKey' ? ' md:col-span-2' : '')}
                  >
                    <label className="form-label">
                      {FIELD_LABEL[k]} <span className="font-normal text-g400">({example})</span>
                    </label>
                    {k === 'publishableKey' ? (
                      <>
                        <PublishableKeyInput
                          value={draft[m][k]}
                          onChange={(v) => setField(m, k, v)}
                          placeholder={example}
                          invalid={Boolean(error)}
                        />
                        <FieldHint error={error}>{stored ? null : 'Not set.'}</FieldHint>
                      </>
                    ) : (
                      <SecretKeyField
                        saved={stored}
                        value={draft[m][k]}
                        onChange={(v) => setField(m, k, v)}
                        placeholder={example}
                        error={error}
                        emptyHint={
                          k === 'webhookSecret'
                            ? 'Needed for subscription renewals and failed-payment updates. Create webhook below fills it in for you.'
                            : 'Not set.'
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <StripeWebhookSetup
              mode={m}
              savedSecretKey={saved[m].secretKey}
              blockedReason={dirty ? 'Save or discard your key changes first.' : null}
              onSettingsChanged={apply}
            />
          </div>
        ))}

        <div className="flex items-center gap-3 pt-2">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!dirty || hasErrors || !modeReady || saving}
            style={!dirty || hasErrors || !modeReady ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <Icon name="check-circle" size={16} color="#fff" />
            {saving ? 'Saving...' : 'Save Stripe settings'}
          </button>
          {dirty && !saving && (
            <button className="btn btn-secondary" onClick={() => apply(saved)}>
              Discard changes
            </button>
          )}
        </div>
      </div>

      {/* ── Side panel ── */}
      <div className="flex flex-col gap-6">
        <div
          className="flex items-start gap-2.5 px-4 py-3.5 rounded-2xl text-[12.5px] leading-relaxed animate-fadeIn"
          style={{ background: 'var(--brand-bg)', border: '1px solid rgba(225,1,116,0.18)', color: 'var(--g700)' }}
        >
          <Icon name="info" size={16} color="var(--brand)" />
          <span>
            Changes apply immediately. The website and mobile apps read the publishable key
            from <code className="font-mono text-[11.5px]">GET /api/stripe/config</code>, and
            active subscription plans are re-published to Stripe whenever the active secret
            key changes.
          </span>
        </div>

        <div className="card p-5 animate-fadeIn" style={{ animationDelay: '60ms' }}>
          <div className="flex items-center gap-2.5 mb-4">
            <Icon name="credit-card" size={18} color="var(--brand)" />
            <span className="text-[15px] font-bold">Current Stripe status</span>
          </div>
          <div className="flex flex-col gap-2">
            <StatusRow label="Active mode">
              <ModeBadge mode={saved.mode} />
            </StatusRow>
            <StatusRow label="Publishable key">
              <span className="font-mono text-[12px]">
                {active.publishableKey ? shortKey(active.publishableKey) : 'Not set'}
              </span>
            </StatusRow>
            <StatusRow label="Webhook secret">
              {active.webhookSecret ? 'Set' : 'Not set'}
            </StatusRow>
            <StatusRow label="Last updated">
              {saved.updatedAt
                ? new Date(saved.updatedAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : 'Never'}
            </StatusRow>
          </div>
        </div>

        <div className="card p-5 animate-fadeIn" style={{ animationDelay: '120ms' }}>
          <div className="flex items-center gap-2.5 mb-4">
            <Icon name="key" size={18} color="var(--brand)" />
            <span className="text-[15px] font-bold">Where to find your keys</span>
          </div>
          <ol className="flex flex-col gap-3 text-[13px] text-g700">
            {[
              <>
                In the Stripe Dashboard, open <strong>Developers → API keys</strong>.
              </>,
              <>
                Turn <strong>Test mode</strong> on to copy <code className="font-mono">pk_test_</code> /{' '}
                <code className="font-mono">sk_test_</code> keys, or off for{' '}
                <code className="font-mono">pk_live_</code> / <code className="font-mono">sk_live_</code>.
              </>,
              <>
                Save the keys, then click <strong>Create webhook</strong> under each mode. It sets
                up the webhook in Stripe and fills in the signing secret (
                <code className="font-mono">whsec_</code>) for you.
              </>,
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-bg text-brand text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="leading-relaxed pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary w-full justify-center mt-5"
          >
            Open Stripe Dashboard
            <Icon name="external-link" size={14} />
          </a>
        </div>
      </div>

      <ConfirmDialog
        open={confirmLive}
        title="Switch to live mode?"
        variant="primary"
        confirmLabel="Switch to live"
        cancelLabel="Stay in test"
        busy={saving}
        message={
          <>
            Customers will be charged <strong style={{ color: 'var(--ink)' }}>real money</strong>{' '}
            using your live keys, and Stripe test cards will stop working.
          </>
        }
        onConfirm={save}
        onCancel={() => {
          setConfirmLive(false);
          setMode(saved.mode);
        }}
      />
    </div>
  );
};

export default StripeSettings;
